import { Capacitor } from "@capacitor/core";
import { WorkoutState } from "@sworkit/workout/dist/types/supporting/workout-player-state";
import SwWorkout from "@sworkit/workout";
import { translate } from "../translate";
import { WorkoutLog } from "@sworkit/api";
import swWatchState from "../../components/pages/sw-workout-player-page/sw-watch-heartbeat/sw-watch.store";
import SworkitAppleWatch from ".";

let workoutStarted = false;
enum WatchStates {
  notInstalled = "notInstalled",
  lostConnectivity = "lostConnectivity",
  regainedConnectivity = "regainedConnectivity",
  inactive = "inactive",
  deactivated = "deactivated",
  sessionActivated = "sessionActivated",
}

enum WatchCommands {
  playPause = "toggleTimer",
  rewind = "back",
  fastForward = "skip",
  workoutModel = "requestWorkoutModel",
  authHeartRate = "authHeartRate",
  heartRate = "heartRateSamples",
  avgHeartRate = "avgHeartRate",
  watchState = "watchState",
}

const getWorkoutState = (state: WorkoutState) => {
  if (state.workoutStarted && !state.hasWorkoutEnded) {
    if (state.isPlaying) {
      return 3;
    } else {
      return 4;
    }
  } else {
    if (state.workoutName && !state.isPlaying) {
      return 1;
    }
  }
  return 0;
};

const hasPreviousExercise = (state: WorkoutState) => {
  return state.currentExerciseIndex !== 0;
};

export async function initWatchTranslations() {
  if (Capacitor.getPlatform() !== "ios") return;
  try {
    await SworkitAppleWatch.initWatchTranslations({
      translations: {
        intro: translate("CHOOSEWATCH"),
        congrats: translate("CONGRATS"),
        bpm: translate("BPM"),
        avgbpm: translate("AVGBPM"),
        peakbpm: translate("PEAKBPM"),
        minutes: translate("MINUTE", 2),
        calories: translate("CALORIES_BG"),
      },
    });
  } catch (err) {
    console.error("initWatchTranslations error", err);
  }
}

export async function initWatch(sw: SwWorkout) {
  try {
    if (Capacitor.getPlatform() === "ios") {
      SworkitAppleWatch.initWatch({}, (commandObj: object) => {
        const commandDict = commandObj["commandDict"];
        const command: WatchCommands = commandDict["commandName"];
        const watchState: WatchStates = commandDict["watchState"];

        switch (command) {
          case "skip":
            sw.skip();
            break;
          case "back":
            sw.previous();
            break;
          case "toggleTimer":
            if (sw.isPlaying) {
              sw.pause();
            } else {
              sw.play();
            }
            break;
          case "authHeartRate":
            console.debug("auth heart rate");
            break;
          case "avgHeartRate":
            console.debug("avgHeartRate");
            swWatchState.avgHeartRate = commandDict["avgHeartRate"];
            break;
          case "heartRateSamples":
            const heartRateSamples = commandDict["heartRateSamples"];
            swWatchState.heartRate = Array.isArray(heartRateSamples)
              ? heartRateSamples[heartRateSamples.length - 1]
              : 0;
            console.debug("heart rate samples", heartRateSamples);
            break;
          default:
            break;
        }

        if (watchState) {
          console.debug("WATCH STATE", watchState);
          if (watchState === "regainedConnectivity") {
            swWatchState.watchConnected = true;
          }
          if (watchState === "lostConnectivity") {
            swWatchState.watchConnected = false;
          }
        }
      });
      // only begin workout if watch is connected
      beginWatchWorkout(sw);
    }
  } catch (err) {
    console.debug("initWatch error", err);
  }
}

async function beginWatchWorkout(sw: SwWorkout) {
  const workoutState = sw.workoutPlayerState.getState(sw);
  try {
    workoutStarted = true;
    const appleWatchState = {
      activityType:
        sw.workout["appleActivityHK"] ||
        "HKWorkoutActivityTypeFunctionalStrengthTraining",
      workoutState: 0, // enum for UI
      workoutName: workoutState.workoutName,
      exerciseName: "",
      hasNextExercise: true,
      hasPreviousExercise: false,
      workoutComplete: false, // end modal is open, the user is currently deciding if they should end or not
      workoutOver: false, // workout is finished / run out of time, or you have chosen to end
      avgHeartRate: 0, // need to calculate
      peakHeartRate: 0,
      minutesComplete: 0,
      caloriesBurned: 0,
      startDate: new Date(),
    };
    await SworkitAppleWatch.syncWorkoutState({
      state: appleWatchState,
    });
  } catch (err) {
    console.debug("beginWatchWorkout error", err);
  }
}

export async function updateWatchState(state: WorkoutState) {
  try {
    if (!workoutStarted) return;
    const appleWatchState = {
      workoutState: getWorkoutState(state) || 3, // enum for UI
      workoutName: state.workoutName || "",
      exerciseName: state.exerciseName || "",
      hasNextExercise: true,
      hasPreviousExercise: hasPreviousExercise(state) || false,
      workoutComplete: state.hasWorkoutEnded,
      workoutOver: state.hasWorkoutEnded,
    };
    await SworkitAppleWatch.syncWorkoutState({
      state: appleWatchState,
    });
  } catch (err) {
    console.debug("updateWatchState error", err);
  }
}

export async function endWatchWorkout(log: WorkoutLog) {
  try {
    const appleWatchState = {
      workoutState: 0, // enum for UI
      workoutComplete: true,
      workoutOver: true, // workout is finished / run out of time, or you have chosen to end
      minutesComplete: log.minutesCompleted || 0,
      caloriesBurned: log.calories || 0,
    };
    await SworkitAppleWatch.syncWorkoutState({
      state: appleWatchState,
    });
    workoutStarted = false;
  } catch (err) {
    console.debug("endWatchWorkout error", err);
  }
}
